import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

// Augmenta AxiosRequestConfig con flag per chiamate "demo" (pagina /dev/*):
//   _noAuthRedirect: evita il login() su 401 (la demo gestisce 401 con UI di
//                    fallback invece di buttare l'utente su Keycloak)
//   _demoFallbackRole: se il profilo non ha activeRole, inietta "OTA" sull'
//                    header X-Active-Role per non far fallire la request lato BE
declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface AxiosRequestConfig {
    _noAuthRedirect?: boolean;
    _demoFallbackRole?: boolean;
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface InternalAxiosRequestConfig {
    _noAuthRedirect?: boolean;
    _demoFallbackRole?: boolean;
  }
}
import { env } from "@/lib/env";
import { ensureFreshToken, getAccessToken, login } from "@/auth/keycloak";
import { store } from "@/store";
import { ensureCancelKey, ensureAmendKey } from "@/store/slices/bookingDraftSlice";
import { pushToast } from "@/store/slices/uiSlice";
import i18n from "@/i18n/config";

const baseURL = env.VITE_BE_URL;

/** Endpoint che richiedono Idempotency-Key. Match contro il path della
 * request (es. `/api/v1/book`, `/api/v1/bookings/<uuid>` per cancel,
 * `/api/v1/bookings/<uuid>/amend`, `/api/v1/booking-groups/<uuid>`). */
const IDEMPOTENT_RULES: Array<{ method: string; test: (p: string) => boolean }> = [
  { method: "post", test: (p) => p.endsWith("/book") || p === "/book" },
  { method: "post", test: (p) => /\/bookings\/[^/]+\/amend$/.test(p) },
  { method: "delete", test: (p) => /\/bookings\/[^/]+$/.test(p) },
  { method: "delete", test: (p) => /\/booking-groups\/[^/]+$/.test(p) },
];

export const apiClient = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  const { profile, settings } = store.getState();
  // X-Active-Role è popolato solo dopo che `useOnboardingBootstrap` ha
  // risolto le memberships via `GET /me/profile`. Chiamate fatte PRIMA
  // del bootstrap (es. /me/profile stesso) vanno volutamente senza header:
  // gli endpoint di bootstrap usano `get_authenticated_user` lato BE, gli
  // altri risponderanno 400 — sintomo corretto, non un 403 silenzioso.
  //
  // Eccezione: chiamate marcate `_demoFallbackRole` (la pagina /dev/ai-mapper)
  // ricevono "OTA" come default se il profilo non è ancora bootstrappato.
  if (profile.activeRole) {
    config.headers.set("X-Active-Role", profile.activeRole);
  } else if (config._demoFallbackRole) {
    config.headers.set("X-Active-Role", "OTA");
  }
  if (profile.externalId) {
    config.headers.set("X-External-Id", profile.externalId);
  }

  const path = (config.url ?? "").replace(baseURL, "");
  const method = config.method?.toLowerCase() ?? "";
  const needsIdempotency = IDEMPOTENT_RULES.some(
    (r) => r.method === method && r.test(path),
  );
  if (needsIdempotency && !config.headers.has("Idempotency-Key")) {
    // B-I: idempotency-key persistente per cancel/amend (oltre che per /book).
    // Difende contro retry user-driven (refresh pagina, doppio click).
    // - /book: riusa draft.idempotencyKey (già esistente).
    // - DELETE /bookings/{id}, POST /bookings/{id}/amend: persisti per
    //   (booking_id, action) in Redux bookingDraft.cancel/amendIdempotencyKeys.
    const isBookEndpoint = method === "post" && path.endsWith("/book");
    const cancelMatch =
      method === "delete" && /\/bookings\/([0-9a-fA-F-]+)$/.exec(path);
    const amendMatch =
      method === "post" && /\/bookings\/([0-9a-fA-F-]+)\/amend$/.exec(path);

    let key: string | undefined;
    if (isBookEndpoint) {
      key = store.getState().bookingDraft.idempotencyKey;
    } else if (cancelMatch) {
      const bid = cancelMatch[1];
      const existing = store.getState().bookingDraft.cancelIdempotencyKeys?.[bid];
      if (existing) {
        key = existing;
      } else {
        store.dispatch(ensureCancelKey({ bookingId: bid }));
        key = store.getState().bookingDraft.cancelIdempotencyKeys?.[bid];
      }
    } else if (amendMatch) {
      const bid = amendMatch[1];
      const existing = store.getState().bookingDraft.amendIdempotencyKeys?.[bid];
      if (existing) {
        key = existing;
      } else {
        store.dispatch(ensureAmendKey({ bookingId: bid }));
        key = store.getState().bookingDraft.amendIdempotencyKeys?.[bid];
      }
    }
    config.headers.set("Idempotency-Key", key ?? crypto.randomUUID());
  }

  if (settings.language) {
    config.headers.set("Accept-Language", settings.language);
  }

  return config;
});

let refreshPromise: Promise<string | undefined> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshPromise ??= ensureFreshToken(30);
      const token = await refreshPromise;
      refreshPromise = null;
      if (token) {
        original.headers.set("Authorization", `Bearer ${token}`);
        return apiClient(original);
      }
      // Chiamate "demo" (es. /dev/ai-mapper) NON devono triggerare il redirect
      // Keycloak — la pagina mostra fallback graceful invece. Tutte le altre
      // proseguono con la login() che è il comportamento standard.
      if (!original._noAuthRedirect) {
        login();
      }
      return Promise.reject(error);
    }

    if (status === 503) {
      store.dispatch(
        pushToast({
          tone: "warning",
          title: i18n.t("errors.serverUnavailable"),
        }),
      );
    } else if (status === 502 || status === 504) {
      store.dispatch(
        pushToast({
          tone: "error",
          title: i18n.t("errors.network"),
          description: error.message,
        }),
      );
    }

    return Promise.reject(error);
  },
);

export type ApiErrorBody = {
  detail?: string | { msg?: string }[];
  error?: string;
  message?: string;
};

export class ApiError extends Error {
  status: number;
  body?: ApiErrorBody;
  constructor(status: number, body: ApiErrorBody | undefined, message?: string) {
    super(message ?? extractMessage(body) ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function extractMessage(body?: ApiErrorBody): string | undefined {
  if (!body) return undefined;
  if (typeof body.detail === "string") return body.detail;
  if (Array.isArray(body.detail)) {
    return body.detail
      .map((d) => (typeof d === "object" && d.msg ? d.msg : ""))
      .filter(Boolean)
      .join(", ");
  }
  return body.error ?? body.message;
}

/**
 * Orval mutator: this is the function imported by every generated hook.
 * It returns a Promise<T> with the response body.
 */
export const axiosInstance = <T = unknown>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise: Promise<T> & { cancel?: () => void } = apiClient
    .request<T>({ ...config, ...options, cancelToken: source.token })
    .then((res: AxiosResponse<T>) => res.data)
    .catch((err: AxiosError<ApiErrorBody>) => {
      if (err.response) {
        throw new ApiError(err.response.status, err.response.data, err.message);
      }
      throw err;
    });
  promise.cancel = () => source.cancel("query-cancelled");
  return promise;
};

export type ErrorType<E> = AxiosError<E>;
export type BodyType<B> = B;

export default axiosInstance;
